import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import { FieldItem, Values } from '@lib/interfaces/form';
import Modal from '@lib/components/Modal/Modal';
import TabBar from '@lib/components/TabBar/TabBar';
import Table from '@lib/components/Table/Table';
import Form from '@lib/components/ReactHookForm/FormContainer';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { useTableItemsBulkSelectWithAllIds } from '@lib/hooks/useTableItemsBulkSelect';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { ModalMode } from '@lib/components/Modal/enums';
import Search from '@lib/components/Search/Search';
import { BaseItem, Column } from '@lib/components/Table/types';
import tableItemsBulkAction from '@lib/utils/tableItemsBulkAction';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import {
  OrderingState,
  PaginationState,
  SearchStringState,
} from '@lib/enums/urls';
import ModalTableEmptyState from './ModalTableEmptyState';
import getTableTabs from './utils/getTableTabs';
import { TableTabs } from './enums';
import {
  FilterState,
  ActiveTabState,
} from './hooks/useModalTableMultiselectStates';
import styles from './ModalTableMultiselect.module.scss';

interface ModalTableMultiselectProps<T extends BaseItem> {
  activeTabState: ActiveTabState;
  caption?: string;
  columns: Column<T>[];
  data: T[];
  dataSelected: T[];
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  filterFormFields: FieldItem[];
  filterState: FilterState;
  filterStateSelected: FilterState;
  isOpen: boolean;
  loading?: boolean;
  loadingSelected?: boolean;
  onClose: () => void;
  onIsFetchingAllIdsChange?: (isFetching: boolean) => void;
  onSelect: (values: string[], sites: Values[]) => void;
  onSelectedIdsChange?: (ids: string[]) => void;
  orderingState: OrderingState;
  orderingStateSelected: OrderingState;
  paginationState: PaginationState;
  paginationStateSelected: PaginationState;
  searchPleceholder: string;
  searchState: SearchStringState;
  searchStateSelected: SearchStringState;
  selected?: string[];
  firstLoading?: boolean;
  firstLoadingSelected?: boolean;
  totalEntries: number;
  totalEntriesSelected: number;
}

function ModalTableMultiselect<T extends BaseItem>({
  activeTabState,
  caption = '',
  columns,
  data,
  dataSelected,
  fetchIds,
  filterFormFields,
  filterState,
  filterStateSelected,
  firstLoading,
  firstLoadingSelected,
  isOpen,
  loading = false,
  loadingSelected = false,
  onClose,
  onIsFetchingAllIdsChange,
  onSelect,
  onSelectedIdsChange,
  orderingState,
  orderingStateSelected,
  paginationState,
  paginationStateSelected,
  searchPleceholder,
  searchState,
  searchStateSelected,
  selected,
  totalEntries,
  totalEntriesSelected,
}: ModalTableMultiselectProps<T>) {
  const { t } = useTranslation();

  const [searchValue, onSearchHandler] = searchState;
  const [searchValueSelected, onSearchHandlerSelected] = searchStateSelected;
  const [activeTab, setActiveTab] = activeTabState;

  const useBulkSelectResult = useTableItemsBulkSelectWithAllIds<T>({
    fetchIds,
  });

  const {
    handleUnselectAllTableItems,
    initializeSelectedItems,
    selectedTableItems,
    getSelectedItemsCount,
    isResolvingAllItemIds,
    areAllItemsMaterialized,
  } = useBulkSelectResult;
  const [hasInitialized, setHasInitialized] = useState(false);

  const selectedIds = useMemo(
    () => selectedTableItems.map((item) => String(item.id)),
    [selectedTableItems],
  );

  useEffect(() => {
    if (onIsFetchingAllIdsChange) {
      onIsFetchingAllIdsChange(isResolvingAllItemIds);
    }
  }, [isResolvingAllItemIds, onIsFetchingAllIdsChange]);

  useEffect(() => {
    if (isOpen && onSelectedIdsChange) {
      onSelectedIdsChange(selectedIds);
    }
  }, [isOpen, onSelectedIdsChange, selectedIds]);

  // Initialize selected items when modal opens with selected IDs
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (selected && selected.length > 0) {
        // Always initialize with partial items.
        // The full items will be present in the `data` prop for the "All" tab,
        // and fetched for the `dataSelected` prop for the "Selected" tab.
        // The bulk selection logic only needs the IDs to function correctly.
        const itemsToSelect = selected.map((id) => ({ id }) as T);
        initializeSelectedItems(itemsToSelect);
      }
      setHasInitialized(true);
    } else if (!isOpen && hasInitialized) {
      // Reset initialization flag when modal closes
      setHasInitialized(false);
    }
  }, [isOpen, selected, hasInitialized, initializeSelectedItems]);

  const handleTabChange = (value: TableTabs) => {
    setActiveTab(value);
  };

  const [filterValues, setFilterValues] = filterState;
  const [filterValuesSelected, setFilterValuesSelected] = filterStateSelected;

  // Calculate selected counter using the most accurate total available.
  // Once the hook materializes every id we pivot to the enumerated list; otherwise we fall back to totals.
  const selectedCounterBase = areAllItemsMaterialized
    ? selectedTableItems.length
    : totalEntriesSelected || totalEntries;
  const selectedCounter = getSelectedItemsCount(selectedCounterBase);
  const tabItems = getTableTabs({
    t,
    allCounter: totalEntries,
    selectedCounter,
  });

  // Determine which data, pagination, search, and ordering to use based on active tab
  const isSelectedTab = activeTab === TableTabs.Selected;
  const displayData = isSelectedTab ? dataSelected : data;
  const displayLoading = isSelectedTab ? loadingSelected : loading;
  const displayFirstLoading = isSelectedTab
    ? firstLoadingSelected
    : firstLoading;
  const displayTotalEntries = isSelectedTab
    ? totalEntriesSelected
    : totalEntries;
  const displayPaginationState = isSelectedTab
    ? paginationStateSelected
    : paginationState;
  const displaySearchValue = isSelectedTab ? searchValueSelected : searchValue;
  const displaySearchHandler = isSelectedTab
    ? onSearchHandlerSelected
    : onSearchHandler;
  const displayOrderingState = isSelectedTab
    ? orderingStateSelected
    : orderingState;

  const displayColumns = columns;

  const displayFilterValues = isSelectedTab
    ? filterValuesSelected
    : filterValues;

  let filterCounter = 0;
  Object.values(displayFilterValues).forEach((filterValue) => {
    if (_get(filterValue, 'length')) {
      filterCounter += 1;
    }
  });

  // Actions
  // Debounce filter form submissions to reduce rapid state updates on change
  const debouncedFilterSubmit = useMemo(
    () =>
      _debounce((values: Values, tab: TableTabs) => {
        if (tab === TableTabs.Selected) {
          setFilterValuesSelected(values);
        } else {
          setFilterValues(values);
        }
      }, 300),
    [setFilterValues, setFilterValuesSelected],
  );

  useEffect(() => {
    return () => debouncedFilterSubmit.cancel();
  }, [debouncedFilterSubmit]);

  const handleFilterFormSubmit = useCallback(
    (values: Values) => {
      debouncedFilterSubmit(values, activeTab);
    },
    [debouncedFilterSubmit, activeTab],
  );

  const handleResetFilter = () => {
    if (activeTab === TableTabs.Selected) {
      setFilterValuesSelected({});
    } else {
      setFilterValues({});
    }
  };

  const handleResetSearch = () => {
    if (activeTab === TableTabs.Selected) {
      onSearchHandlerSelected('');
    } else {
      onSearchHandler('');
    }
  };

  const handleModalClose = () => {
    // Clear selections when modal closes without confirming
    handleUnselectAllTableItems();
    onClose();
  };

  const onGetAllIds = useCallback(
    () => Promise.resolve(selectedIds),
    [selectedIds],
  );

  const handleConfirmSelection = () => {
    // Note: fetchIds will be called by tableItemsBulkAction via onGetAllIds if needed
    tableItemsBulkAction<T>({
      action: (idsValues?: string[]) => {
        onSelect(
          idsValues || [],
          _get(displayFilterValues, 'sites') as Values[],
        );
        handleModalClose();
      },
      useBulkSelectResult,
      onGetAllIds,
    });
  };

  const isContentVisible = !!displayTotalEntries && !!displayData.length;
  const isEmptyStateVisible = !isContentVisible && !displayFirstLoading;
  const isSearchApplied = !!displaySearchValue;
  const isFilterApplied = !!filterCounter;
  const isEmptyState =
    !displayLoading &&
    isEmptyStateVisible &&
    !isSearchApplied &&
    !isFilterApplied;
  const isEmptyStateBySearch = isEmptyStateVisible && isSearchApplied;
  const isEmptyStateByFilter = isEmptyStateVisible && isFilterApplied;
  const emptyState = (
    <ModalTableEmptyState
      isEmptyState={isEmptyState}
      isEmptyStateBySearch={isEmptyStateBySearch}
      isEmptyStateByFilter={isEmptyStateByFilter}
      tabParam={activeTab}
      onResetFilter={handleResetFilter}
      onResetSearch={handleResetSearch}
    />
  );

  const actions = (
    <ActionsRow
      actions={[
        <Button
          key="secondary"
          buttonText={t('cancel')}
          buttonType={ButtonTypes.secondaryOutlined}
          onClick={handleModalClose}
          fullWidth
        />,
        <Button
          key="primary"
          buttonText={t('confirm-selection')}
          buttonType={ButtonTypes.primaryFilled}
          onClick={handleConfirmSelection}
          fullWidth
        />,
      ]}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      caption={caption}
      mode={ModalMode.FullScreen}
      modalContentClassName={styles.modalContentClassName}
      fixedActions={actions}
    >
      <div>
        <TabBar
          items={tabItems}
          active={activeTab}
          onActiveChange={({ value }) => handleTabChange(value as TableTabs)}
          useSidePaddings={false}
        />
        <Search
          key={`search-${activeTab}`}
          placeholder={searchPleceholder}
          useSearchParam={false}
          value={displaySearchValue}
          onChange={displaySearchHandler}
        />
        <div className={styles.filterWrap}>
          <Form
            fields={filterFormFields}
            defaultValues={displayFilterValues}
            formId="modal-table-filter"
            onSubmit={handleFilterFormSubmit}
            submitOnChange
            formItemsClassName={styles.formItemsClassName}
          />
        </div>
        <Table<T>
          columns={displayColumns}
          data={displayData}
          emptyState={emptyState}
          hasSelectColumn
          loading={displayLoading}
          orderingState={displayOrderingState}
          paginationState={displayPaginationState}
          totalEntries={displayTotalEntries}
          useBulkSelectResult={useBulkSelectResult}
        />
      </div>
    </Modal>
  );
}

export default memo(ModalTableMultiselect) as <T extends BaseItem>(
  props: ModalTableMultiselectProps<T>,
) => React.ReactElement;

export { useModalTableMultiselectStates } from './hooks/useModalTableMultiselectStates';
export { default as useModalTableMultiselect } from './hooks/useModalTableMultiselect';
export type {
  ModalTableMultiselectStates,
  FilterState,
  ActiveTabState,
} from './hooks/useModalTableMultiselectStates';
export { default as useModalTableAllTabManager } from './hooks/useModalTableAllTabManager';
